import { useState, useEffect } from 'react';
import classNames from 'classnames';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import { motion } from 'framer-motion';

import { useMainMachine } from '../../../utils/useMainMachine';
import { usePublishMachine } from '../../../utils/usePublishMachine';
import { ACTIONS, STATES, PUBLISH_ACTIONS, PUBLISH_STATES } from '../../../state';
import { BUTTON_TYPES } from '../../constants';

import { CloseButton } from '../basic-ui/CloseButton';
import { DisclosureButton } from '../basic-ui/DisclosureButton';
import { Input } from '../basic-ui/Input';
import { Button } from '../basic-ui/Button';
import { Spinner } from '../basic-ui/Spinner';
import { splitAt } from '../../../utils/string';

import CopyIcon from '@crazyegginc/hatch/dist/images/icon-copy.svg?react';
import TickIcon from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg?react';

const validationSchema = yup.object().shape({
  email: yup.string().required('Please provide an email.').email('Please provide a valid email.'),
});

export function Publish() {
  const { useSelector } = useMainMachine();
  const { usePublishMachineSelector } = usePublishMachine();

  const visible = useSelector((state) =>
    state.matches({ [STATES.INITIALIZED]: { [STATES.EDITOR]: STATES.PUBLISH_STEPS } }),
  );
  const modalFlow = usePublishMachineSelector((state) => state?.context.hasPublishedBefore);

  if (!visible) return null;

  if (modalFlow) {
    return <ModalPublish />;
  }

  return <FullPublish />;
}

export function ModalPublish() {
  const { machine } = useMainMachine();
  const { usePublishMachineSelector } = usePublishMachine();

  const needsToVerify = usePublishMachineSelector((state) =>
    state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.NEEDS_VERIFICATION }),
  );
  const needsApproval = usePublishMachineSelector((state) =>
    state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.NEEDS_APPROVAL }),
  );
  const isLoading = usePublishMachineSelector(
    (state) =>
      state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.VERIFYING_OR_CREATING_USER }) ||
      state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.CHECKING_PERMISSIONS }),
  );
  const completed = usePublishMachineSelector((state) => !state.matches(PUBLISH_STATES.STEP_1));

  if (completed) return null;

  function content() {
    if (isLoading)
      return (
        <div className="mt-5">
          <Step1Loading />
        </div>
      );
    if (needsApproval) return <Step1Permission />;
    if (needsToVerify) return <Step1VerifyEmail />;
    return (
      <>
        <div className="text-xs font-semibold text-white mb-2.5">Email</div>
        <Step1EmailEntry />
      </>
    );
  }

  return (
    <div className="fixed mr-5 py-5 pr-2.5 bottom-[60px] bg-gray-900" data-testid="publish-step-modal">
      <Box active={true}>
        <CloseButton buttonClass="!top-4 !right-4 !h-3.5 !w-3.5" onClick={() => machine.send({ type: ACTIONS.BACK })} />

        {content()}
      </Box>
    </div>
  );
}

export function FullPublish() {
  const [open, setOpen] = useState(true);
  const { machine } = useMainMachine();
  const { usePublishMachineSelector } = usePublishMachine();

  const step = usePublishMachineSelector((state) => {
    if (state.matches(PUBLISH_STATES.STEP_1)) return 1;
    if (state.matches(PUBLISH_STATES.STEP_2)) return 2;
    if (state.matches(PUBLISH_STATES.STEP_3)) return 3;
  });

  return (
    <div className="flex flex-col" data-testid="publish-section">
      <div className="h-px bg-gray-600 my-5 -mx-5" />

      <div className="flex items-center justify-between">
        <div className="w-full">
          <button type="button" onClick={() => machine.send({ type: ACTIONS.BACK })}>
            Back
          </button>
        </div>
        <div className="w-full flex justify-center font-semibold whitespace-nowrap">Publish - Step {step} of 3</div>
        <div className="w-full flex justify-end">
          <DisclosureButton open={open} onClick={() => setOpen(!open)} contentId="publish-content" />
        </div>
      </div>
      <div className="overflow-hidden">
        <motion.div
          initial={false}
          animate={
            open
              ? {
                  height: 'auto',
                  opacity: 1,
                }
              : {
                  height: 0,
                  opacity: 0,
                }
          }
          transition={{ duration: 0.4, ease: 'linear' }}
          className="mt-2.5 flex flex-col space-y-[15px]"
          id="publish-content"
          data-testid="publish-content"
        >
          <Step1 />
          <Step2 />
          <Step3 />
        </motion.div>
      </div>
    </div>
  );
}

function Step1() {
  const { usePublishMachineSelector } = usePublishMachine();

  const active = usePublishMachineSelector((state) => state.matches(PUBLISH_STATES.STEP_1));

  const needsApproval = usePublishMachineSelector((state) =>
    state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.NEEDS_APPROVAL }),
  );
  const needsToVerify = usePublishMachineSelector((state) =>
    state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.NEEDS_VERIFICATION }),
  );
  const isLoading = usePublishMachineSelector(
    (state) =>
      state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.VERIFYING_OR_CREATING_USER }) ||
      state.matches({ [PUBLISH_STATES.STEP_1]: PUBLISH_STATES.CHECKING_PERMISSIONS }),
  );

  if (isLoading) {
    return (
      <Box active={active}>
        <div className="flex justify-between mb-2.5">
          <span>Step 1</span>
          <span>One time only</span>
        </div>

        <Step1Loading />
      </Box>
    );
  }

  if (needsApproval) {
    return (
      <Box active={active}>
        <Step1Permission />
      </Box>
    );
  }

  if (needsToVerify) {
    return (
      <Box active={active}>
        <div className="flex justify-between mb-2.5">
          <span>Step 1</span>
        </div>
        <Step1VerifyEmail />
      </Box>
    );
  }

  return (
    <Box active={active}>
      <div className="flex justify-between mb-2.5">
        <span>Step 1</span>
        <span>One time only</span>
      </div>

      <Step1EmailEntry />
    </Box>
  );
}

function Step1Loading() {
  return (
    <div className="flex justify-center w-full text-sm text-white font-semibold space-x-2 items-center">
      <Spinner className="w-3.5 h-3.5" />
      <span>Checking account...</span>
    </div>
  );
}

function Step1Permission() {
  return (
    <>
      <div className="text-white font-semibold text-xs">Permission required</div>
      <div className="text-sm leading-snug">
        A request has been sent to the site owner to grant you permission to publish this page. You will receive an
        email to login once you are approved.
      </div>
      <div className="flex justify-center w-full mt-2.5 text-sm text-white font-semibold space-x-2 items-center">
        <Spinner className="w-3.5 h-3.5" />
        <span>Waiting for approval...</span>
      </div>
    </>
  );
}

function Step1VerifyEmail() {
  const { usePublishMachineSelector, publishMachine } = usePublishMachine();
  const userEmail = usePublishMachineSelector(({ context }) => context.userEmail);
  return (
    <>
      <div className="text-white font-semibold text-xs">Verify your email</div>
      <div className="text-sm">Click the link sent to {userEmail} to login.</div>
      <div className="flex justify-end w-full mt-2.5 text-2xs">
        <button className="hover:text-white" onClick={() => publishMachine.send({ type: PUBLISH_ACTIONS.CLEAR_EMAIL })}>
          Use another email
        </button>
      </div>
    </>
  );
}

function Step1EmailEntry() {
  const { usePublishMachineSelector, publishMachine } = usePublishMachine();

  const completed = usePublishMachineSelector(
    (state) => state?.matches(PUBLISH_STATES.STEP_2) || state?.matches(PUBLISH_STATES.STEP_3),
  );
  const userEmail = usePublishMachineSelector(({ context }) => context.userEmail);

  return (
    <Formik
      initialValues={{ email: completed && userEmail ? userEmail : '' }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        publishMachine.send({ type: PUBLISH_ACTIONS.SUBMIT_EMAIL, userEmail: values.email });
      }}
    >
      {({ values, errors, touched, handleBlur, handleChange }) => (
        <Form>
          <Input
            placeholder="bob@example.com"
            name="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!completed && touched.email && errors.email ? errors.email : null}
            disabled={completed}
          />
          {completed ? null : (
            <div className="text-2xs leading-snug mt-2.5">
              By clicking &apos;Continue&apos; you agree to Crazy Egg&apos;s{' '}
              <a
                href="https://www.crazyegg.com/terms"
                target="_blank"
                rel="noreferrer"
                className="text-white hover:underline"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href="https://www.crazyegg.com/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-white hover:underline"
              >
                Privacy Policy
              </a>
              .
            </div>
          )}
          {completed ? (
            <Button
              type="submit"
              disabled={true}
              className="w-full !bg-blue-500 !border-transparent !text-white opacity-50 mt-2.5"
            >
              <TickIcon className="h-3 w-3 fill-current mr-2" />
              Completed
            </Button>
          ) : (
            <Button type="submit" className="w-full mt-2.5">
              Continue
            </Button>
          )}
        </Form>
      )}
    </Formik>
  );
}

function Step2() {
  const { useSelector } = useMainMachine();
  const { usePublishMachineSelector, publishMachine } = usePublishMachine();

  const installed = usePublishMachineSelector((state) => state?.matches(PUBLISH_STATES.STEP_3));
  const active = usePublishMachineSelector((state) => state?.matches(PUBLISH_STATES.STEP_2));
  const blurred = usePublishMachineSelector((state) => state?.matches(PUBLISH_STATES.STEP_1));

  const accountId = useSelector(({ context }) => context.editor.user_id);
  const scriptId = accountId ? splitAt(4, String(accountId).padStart(8, '0')) : ['0000', '0000'];

  const polling = usePublishMachineSelector(
    (state) =>
      state.matches({ [PUBLISH_STATES.STEP_2]: PUBLISH_STATES.WAITING_FOR_INSTALLATION }) ||
      state.matches({ [PUBLISH_STATES.STEP_2]: PUBLISH_STATES.CHECKING_INSTALLATION }),
  );
  const initialCheck = usePublishMachineSelector(
    (state) =>
      state.matches({ [PUBLISH_STATES.STEP_2]: PUBLISH_STATES.CHECKING_INSTALLATION }) && !state.context.polling,
  );
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  }, [copied]);

  function copyScript(value) {
    navigator.clipboard.writeText(
      `<script type="text/javascript" src="//script.crazyegg.com/pages/scripts/${scriptId[0]}/${scriptId[1]}.js" async="async"></script>`,
    );
    setCopied(value);
    publishMachine.send({ type: PUBLISH_ACTIONS.START_CHECKING_INSTALLATION });
  }

  return (
    <Box active={active}>
      <div className="flex justify-between">
        <span>Step 2</span>
        <span>One time only</span>
      </div>
      <div className="text-xs font-semibold text-white">Install the script</div>
      <div className="mt-1.25">
        To publish the changes install the script at the top of the{' '}
        <span className="font-mono text-white">&lt;HEAD&gt;</span> section just after any{' '}
        <span className="font-mono text-white">&lt;META&gt;</span> tags on your website.
      </div>
      <div
        className={classNames(
          'relative my-2.5 rounded-md bg-gray-500 px-2.5 py-[15px] text-2xs font-mono break-words text-[#96C178]',
          {
            'blur-[2px]': blurred,
          },
        )}
      >
        <button
          type="button"
          onClick={() => copyScript(2)}
          disabled={!!copied || blurred}
          className="absolute top-2 right-2 p-1 text-gray-200 enabled:hover:text-white active:scale-90"
        >
          {copied === 2 ? (
            <TickIcon className="w-3 h-3 fill-current text-green-500" aria-label="copied successfully" />
          ) : (
            <CopyIcon className="w-3.5 h-3.5 fill-current" aria-label="copy to clipboard" />
          )}
        </button>
        <span className="text-white">&lt;</span>
        <span className="text-[#F15D69]">script</span> <span className="text-[#EA9949]">type</span>
        <span className="text-white">=</span>
        &quot;text/javascript&quot; <span className="text-[#EA9949]">src</span>
        <span className="text-white">=</span>
        &quot;//script.crazyegg.com/pages/scripts/{scriptId[0]}/{scriptId[1]}.js&quot;{' '}
        <span className="text-[#EA9949]">async</span>
        <span className="text-white">=</span>
        &quot;async&quot;
        <span className="text-white">&gt;&lt;/</span>
        <span className="text-[#F15D69]">script</span>
        <span className="text-white">&gt;</span>
      </div>
      {(!polling && !installed) || copied === 1 ? (
        <Button className="w-full" onClick={() => copyScript(1)} disabled={copied === 1}>
          {copied === 1 ? 'Copied' : 'Copy script'}
        </Button>
      ) : installed ? (
        <div className="flex items-center justify-center text-sm font-semibold">
          <TickIcon className="h-3 w-3 fill-current mr-2 text-green-500" />
          Script installed
        </div>
      ) : (
        <div className="flex text-sm text-white font-semibold space-x-2 items-center text-center leading-tight mt-2.5 justify-center">
          <Spinner className="w-3.5 h-3.5" />
          <span>{initialCheck ? 'Checking installation...' : 'Waiting for script installation...'}</span>
        </div>
      )}
    </Box>
  );
}

function Step3() {
  const { machine } = useMainMachine();
  const { usePublishMachineSelector } = usePublishMachine();
  const active = usePublishMachineSelector((state) => state?.matches(PUBLISH_STATES.STEP_3));

  return (
    <Box active={active}>
      Step 3
      <Button
        className="w-full mt-2.5"
        variant={active ? BUTTON_TYPES.PUBLISH : BUTTON_TYPES.PRIMARY}
        onClick={() => machine.send({ type: ACTIONS.PUBLISH })}
      >
        Publish page
      </Button>
    </Box>
  );
}

function Box({ active, children }) {
  return (
    <div
      className={classNames('rounded-md bg-[#222222] p-[15px] text-2xs text-gray-200 relative', {
        'opacity-60 pointer-events-none': !active,
      })}
    >
      {children}
    </div>
  );
}
